import { cx } from './cx.ts';

/**
 * The board lamp — Dispatch Deck's signature status mark. A small
 * square indicator: lit lamps glow (tone shadow), `off` is a dead lamp
 * in line grey. `pulse` breathes and is reserved for LIVE states
 * (streaming, heartbeat) — at most one pulsing lamp per view region
 * (DESIGN.md §2 "glow only when live").
 */
export type LampTone = 'ok' | 'warn' | 'bad' | 'signal' | 'off';
export type LampSize = 'sm' | 'md';

const TONE_CLASSES: Record<LampTone, string> = {
  ok: 'bg-ok shadow-lamp-ok',
  warn: 'bg-warn shadow-lamp-warn',
  bad: 'bg-bad shadow-lamp-bad',
  signal: 'bg-signal shadow-lamp-signal',
  off: 'bg-line',
};

const SIZE_CLASSES: Record<LampSize, string> = {
  sm: 'h-1.5 w-1.5',
  md: 'h-2 w-2',
};

interface Props {
  readonly tone: LampTone;
  readonly size?: LampSize;
  readonly pulse?: boolean;
  readonly className?: string;
  readonly testId?: string;
}

export function StatusLamp({ tone, size = 'md', pulse = false, className, testId }: Props): JSX.Element {
  return (
    <span
      aria-hidden="true"
      data-testid={testId}
      data-tone={tone}
      className={cx(
        'inline-block shrink-0 rounded-[1.5px]',
        TONE_CLASSES[tone],
        SIZE_CLASSES[size],
        pulse && tone !== 'off' && 'animate-lamp-pulse',
        className,
      )}
    />
  );
}
